import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { springs } from '../../animation';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useCelebration } from '../../hooks/useCelebration';
import { useAuthStore } from '../../hooks/useAuthStore';
import { useChildStore } from '../../hooks/useChildStore';
import AnimatedButton from '../../components/ui/AnimatedButton';
import MindoraCharacter from '../../components/character/MindoraCharacter';
import SpeechBubble from '../../components/ui/SpeechBubble';
import Confetti from '../../components/effects/Confetti';
import Fireworks from '../../components/effects/Fireworks';
import ScreenShake from '../../components/effects/ScreenShake';
import StarBurst from '../../components/effects/StarBurst';
import { chatService } from '../../services';

// Merged phases: prompt → answering → celebration
type Phase = 'prompt' | 'answering' | 'celebration';

const FALLBACK_RIDDLES = [
  "Here's a riddle: I have hands but can't clap. What am I? (A clock!)",
  "Here's one: I have a face but no eyes, hands but no arms. What am I? (A clock!)",
  "Try this: What has legs but doesn't walk? (A table!)",
  "Here's a fun one: What gets wetter the more it dries? (A towel!)",
];

const FUN_FACTS: Record<string, string> = {
  animals: 'Did you know octopuses have THREE hearts?',
  space: 'Did you know a day on Venus is longer than a year on Venus?',
  dinosaurs: 'Did you know the T-Rex had the strongest bite of any land animal ever?',
  drawing: 'Did you know the Mona Lisa has no eyebrows?',
  music: 'Did you know a violin is made of over 70 separate pieces of wood?',
  cooking: 'Did you know honey never goes bad? Even 3,000-year-old honey is still edible!',
  computers: 'Did you know the first computer bug was an actual moth stuck in a computer?',
  stories: 'Did you know the longest novel ever written has over 9 million characters?',
  numbers: 'Did you know if you shuffle a deck of cards, that exact order has probably never existed before?',
  nature: 'Did you know trees talk to each other underground through fungi?',
  history: 'Did you know Cleopatra lived closer in time to the Moon landing than to the Great Pyramid?',
  sports: 'Did you know the first Olympic Games were held in 776 BC in ancient Greece?',
};

function getFirstSessionKey(userId: string) {
  return `mindora-first-session-done-${userId}`;
}

export function isFirstSessionDone(userId: string): boolean {
  return localStorage.getItem(getFirstSessionKey(userId)) === 'true';
}

export default function FirstSessionPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const { selectedChild } = useChildStore();
  const celebration = useCelebration();
  const reduced = useReducedMotion();

  const [phase, setPhase] = useState<Phase>('prompt');
  const [funFact, setFunFact] = useState('');
  const [aiQuestion, setAiQuestion] = useState('');
  const [userAnswer, setUserAnswer] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showStarBurst, setShowStarBurst] = useState(false);

  // Use selectedChild name, fall back to user email
  const childName = selectedChild?.name || user?.email?.split('@')[0] || 'friend';

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Pick a fun fact from stored interests
  useEffect(() => {
    const onboardingData = localStorage.getItem('mindora-onboarding');
    let interests: string[] = [];
    if (onboardingData) {
      try {
        const parsed = JSON.parse(onboardingData);
        if (parsed.state?.childInterests) interests = parsed.state.childInterests;
      } catch { /* ignore */ }
    }
    const interestKey = interests[0] || 'animals';
    setFunFact(FUN_FACTS[interestKey] || FUN_FACTS.animals);
  }, []);

  const handleStartTask = useCallback(async () => {
    if (!user?.id) return;
    setIsSending(true);

    // 3-second timeout for AI request with fallback
    const timeoutId = setTimeout(() => {
      const fallback = FALLBACK_RIDDLES[Math.floor(Math.random() * FALLBACK_RIDDLES.length)];
      setAiQuestion(fallback);
      setPhase('answering');
      setIsSending(false);
    }, 3000);

    try {
      const result = await chatService.sendMessage({
        childId: selectedChild?.id || user.id,
        content: `Give me ONE fun, quick riddle or quiz question appropriate for a child. Keep it short and playful. Just the question, no answer yet.`,
        subject: 'general',
      });

      clearTimeout(timeoutId);
      setAiQuestion(result.aiResponse.content);
      setConversationId(result.conversationId);
      setPhase('answering');
    } catch {
      clearTimeout(timeoutId);
      const fallback = FALLBACK_RIDDLES[Math.floor(Math.random() * FALLBACK_RIDDLES.length)];
      setAiQuestion(fallback);
      setPhase('answering');
    } finally {
      setIsSending(false);
    }
  }, [user?.id, selectedChild?.id]);

  const handleSubmitAnswer = useCallback(async () => {
    if (!user?.id || !userAnswer.trim()) return;
    setIsSending(true);

    try {
      const result = await chatService.sendMessage({
        childId: selectedChild?.id || user.id,
        content: userAnswer,
        conversationId: conversationId || undefined,
        subject: 'general',
      });

      setAiResponse(result.aiResponse.content);
    } catch {
      setAiResponse("Great try! You're really smart! I can tell we're going to have so much fun learning together!");
    } finally {
      setIsSending(false);
      setPhase('celebration');
    }
  }, [user?.id, selectedChild?.id, userAnswer, conversationId]);

  // Trigger celebration effects — faster timing
  useEffect(() => {
    if (phase !== 'celebration') return;

    // 200ms trigger
    const celebTimer = setTimeout(() => {
      celebration.celebrate('streak');
      setShowCelebration(true);
    }, 200);

    // StarBurst at 600ms
    const starTimer = setTimeout(() => setShowStarBurst(true), 600);

    return () => {
      clearTimeout(celebTimer);
      clearTimeout(starTimer);
    };
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFinish = () => {
    if (user?.id) {
      localStorage.setItem(getFirstSessionKey(user.id), 'true');
    }
    navigate('/learn');
  };

  return (
    <ScreenShake trigger={showCelebration} intensity={phase === 'celebration' ? 1 : 0}>
      <div className="min-h-screen bg-kid-bg texture-paper-grain flex flex-col items-center justify-center px-6 py-12 relative overflow-hidden">
        <Confetti trigger={showCelebration} count={50} />
        <Fireworks trigger={showCelebration} bursts={6} />

        <AnimatePresence mode="wait">
          {/* Phase 1: Prompt (merged greeting + task) */}
          {phase === 'prompt' && (
            <motion.div
              key="prompt"
              className="flex flex-col items-center text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                initial={{ y: 100, opacity: 0, scale: 0.5 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                transition={reduced ? { duration: 0.3 } : springs.bouncy}
              >
                <MindoraCharacter mood="happy" size="xl" />
              </motion.div>

              <motion.div
                className="mt-6 max-w-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={reduced ? { duration: 0.3, delay: 0.3 } : { ...springs.gentle, delay: 0.4 }}
              >
                <SpeechBubble>
                  <p className="font-display text-kid-xl text-kid-text">
                    Hey {childName}! {funFact}
                  </p>
                </SpeechBubble>
              </motion.div>

              <motion.div
                className="mt-4 max-w-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={reduced ? { duration: 0.3, delay: 0.8 } : { ...springs.gentle, delay: 1.2 }}
              >
                <SpeechBubble direction="right">
                  <p className="text-kid-text font-kid text-kid-sm">
                    Want to try something fun together?
                  </p>
                </SpeechBubble>
              </motion.div>

              <motion.div
                className="mt-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={reduced ? { duration: 0.3, delay: 1.2 } : { ...springs.gentle, delay: 1.8 }}
              >
                <AnimatedButton
                  variant="marshmallow"
                  className="btn-marshmallow-tangerine"
                  onClick={handleStartTask}
                  disabled={isSending}
                >
                  {isSending ? 'Thinking...' : "Yes, let's go!"}
                </AnimatedButton>
              </motion.div>
            </motion.div>
          )}

          {/* Phase 2: Answering */}
          {phase === 'answering' && (
            <motion.div
              key="answering"
              className="flex flex-col items-center text-center"
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -60 }}
              transition={reduced ? { duration: 0.2 } : springs.pageTurn}
            >
              <MindoraCharacter mood="thinking" size="lg" />

              <div className="mt-6 max-w-sm">
                <SpeechBubble>
                  <p className="text-kid-text font-kid">{aiQuestion}</p>
                </SpeechBubble>
              </div>

              <motion.input
                type="text"
                value={userAnswer}
                onChange={(e) => setUserAnswer(e.target.value)}
                className="input-kid text-kid-lg text-center mt-6 max-w-xs w-full"
                placeholder="Your answer..."
                autoFocus
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              />

              <AnimatePresence>
                {userAnswer.trim().length > 0 && (
                  <motion.div
                    className="mt-4"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                  >
                    <AnimatedButton
                      variant="marshmallow"
                      className="btn-marshmallow-meadow"
                      onClick={handleSubmitAnswer}
                      disabled={isSending}
                    >
                      {isSending ? 'Checking...' : "That's my answer!"}
                    </AnimatedButton>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {/* Phase 3: Celebration — sped up */}
          {phase === 'celebration' && (
            <motion.div
              key="celebration"
              className="flex flex-col items-center text-center relative"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <MindoraCharacter mood="celebrating" size="xl" />

              {aiResponse && (
                <motion.div
                  className="mt-4 max-w-sm"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <SpeechBubble>
                    <p className="text-kid-text font-kid">{aiResponse}</p>
                  </SpeechBubble>
                </motion.div>
              )}

              {/* "You did it!" with StarBurst — 800ms */}
              <motion.div className="relative mt-6">
                <StarBurst trigger={showStarBurst} />
                <motion.h2
                  className="font-display text-kid-3xl text-kid-text"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={reduced ? { duration: 0.3, delay: 0.5 } : { ...springs.bouncy, delay: 0.8 }}
                >
                  You did it!
                </motion.h2>
              </motion.div>

              {/* Finish button — 1.5s */}
              <motion.div
                className="mt-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={reduced ? { duration: 0.3, delay: 1 } : { ...springs.gentle, delay: 1.5 }}
              >
                <AnimatedButton
                  variant="marshmallow"
                  className="btn-marshmallow-meadow"
                  onClick={handleFinish}
                >
                  Let's keep learning!
                </AnimatedButton>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </ScreenShake>
  );
}
